import { useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Sparkle, ArrowRight, CheckCircle } from "@phosphor-icons/react";

/**
 * /forgot-password — public route.
 * User types their email, we POST /api/auth/forgot-password. Backend
 * always returns success (no email enumeration) and sends a Resend
 * email if the account actually exists. We show the same neutral
 * confirmation regardless so a curious visitor can't tell which
 * emails are registered on the platform.
 */
export default function ForgotPassword() {
    const [email, setEmail] = useState("");
    const [submitted, setSubmitted] = useState(false);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const onSubmit = async (e) => {
        e.preventDefault();
        setErr("");
        setBusy(true);
        try {
            await api.post("/auth/forgot-password", { email });
            setSubmitted(true);
        } catch (e) {
            setErr(e?.response?.data?.detail || "Something went wrong. Please try again in a moment.");
        }
        setBusy(false);
    };

    return (
        <div className="min-h-[80vh] grid place-items-center px-4 py-12">
            <div className="w-full max-w-md" data-testid="forgot-password-page">
                <div className="flex items-center gap-2 mb-6 justify-center">
                    <div className="h-10 w-10 rounded-xl bg-[#6C63FF] grid place-items-center text-white">
                        <Sparkle size={20} weight="fill" />
                    </div>
                    <div className="font-display font-black text-xl tracking-tight">
                        <span className="text-[#6C63FF]">Ideas</span><span className="text-[#FF8A00]">Mine</span>
                    </div>
                </div>

                <div className="rounded-2xl border border-border bg-card p-7">
                    {submitted ? (
                        <div className="text-center" data-testid="forgot-password-success">
                            <CheckCircle size={48} weight="duotone" className="text-emerald-500 mx-auto mb-3" />
                            <h1 className="font-display font-black text-2xl mb-2 tracking-tight">Check your inbox</h1>
                            <p className="text-sm text-muted-foreground leading-relaxed">
                                If an account exists for <span className="font-semibold text-foreground">{email}</span>,
                                we've sent a reset link. It will expire in 30 minutes.
                            </p>
                            <p className="text-xs text-muted-foreground mt-4">
                                Didn't get it? Check spam, or{" "}
                                <button
                                    type="button"
                                    onClick={() => setSubmitted(false)}
                                    className="text-[#6C63FF] font-semibold hover:underline"
                                    data-testid="forgot-password-retry"
                                >
                                    try again
                                </button>.
                            </p>
                            <Link
                                to="/auth"
                                className="inline-block mt-6 text-sm text-[#6C63FF] font-semibold hover:underline"
                                data-testid="forgot-password-back-to-login"
                            >
                                ← Back to sign in
                            </Link>
                        </div>
                    ) : (
                        <>
                            <h1 className="font-display font-black text-2xl mb-1 tracking-tight">Forgot password?</h1>
                            <p className="text-sm text-muted-foreground mb-5">
                                Enter the email you used to sign up and we'll send you a reset link.
                            </p>
                            <form onSubmit={onSubmit} className="space-y-4">
                                <div>
                                    <Label htmlFor="forgot-email" className="text-sm font-semibold">Email</Label>
                                    <Input
                                        id="forgot-email"
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                        autoFocus
                                        autoComplete="email"
                                        placeholder="you@example.com"
                                        className="mt-1"
                                        data-testid="forgot-password-email"
                                    />
                                </div>
                                {err && (
                                    <div className="text-sm text-red-500" data-testid="forgot-password-error">{err}</div>
                                )}
                                <Button
                                    type="submit"
                                    disabled={busy || !email}
                                    className="w-full brand-button"
                                    data-testid="forgot-password-submit"
                                >
                                    {busy ? "Sending…" : "Send reset link"} <ArrowRight size={16} className="ml-1" />
                                </Button>
                            </form>
                            <p className="text-xs text-center text-muted-foreground mt-4">
                                Remembered it?{" "}
                                <Link to="/auth" className="text-[#6C63FF] font-semibold hover:underline" data-testid="forgot-password-back-link">
                                    Back to sign in
                                </Link>
                            </p>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
